import React from "react";
import { isPracticeId } from "../../../../onboarding/practice/practiceGuards";
import { usePracticeScope } from "../../../../onboarding/practice/practiceScope";
import { useTranslation } from "react-i18next";
import { primaryText, secondaryText, greyTextColor, textBlue, borderPrimary } from "../../../../../styling/stylingHelper";
import { getPortionsUnitDisplay, formatSubrecipeUnitPrice } from "../../../../../utils/ingredientGrouping";
import { eur } from "../../../../orderhistory/utils";
import SourceRestaurantBadge from "./SourceRestaurantBadge";
import { OverrideTag, OverrideResetButton } from "./ChoiceOverrideControls";

const DeleteIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 5H4.16667H17.5" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.66699 5V3.33333C6.66699 2.89131 6.84259 2.46738 7.15515 2.15482C7.46771 1.84226 7.89164 1.66667 8.33366 1.66667H11.667C12.109 1.66667 12.5329 1.84226 12.8455 2.15482C13.158 2.46738 13.3337 2.89131 13.3337 3.33333V5M15.8337 5V16.6667C15.8337 17.1087 15.6581 17.5326 15.3455 17.8452C15.033 18.1577 14.609 18.3333 14.167 18.3333H5.83366C5.39164 18.3333 4.96771 18.1577 4.65515 17.8452C4.34259 17.5326 4.16699 17.1087 4.16699 16.6667V5H15.8337Z" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8.33301 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M11.667 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

interface SubrecipeData {
    refRowId: string;
    subrecipeId: string;
    name: string;
    isOrphan: boolean;
    ingredients: any[];
    totalCost: number;
    portionsNeeded: number;
    portionsUnit: string;
    sourceRestaurantName?: string;
}

interface SubrecipesSectionProps {
    subrecipes: SubrecipeData[];
    inlineQuantities: Record<string, string>;
    confirmingDeleteKey: string | null;
    onQuantityChange: (subrecipeId: string, value: string) => void;
    onSaveEdit: (subrecipeId: string, qty: string, originalPortions: number) => void;
    onDelete: (subrecipeId: string) => void;
    onConfirmDelete: (subrecipeId: string) => void;
    onClearAll?: () => void;
    headerActions?: React.ReactNode;
    // BYO dish context: subrecipeIds whose portions are a per-dish override,
    // with a reset action back to the shared value. Absent outside dish context.
    overriddenKeys?: Set<string>;
    onResetOverride?: (subrecipeId: string) => void;
}

const SubrecipesSection: React.FC<SubrecipesSectionProps> = ({
    subrecipes, inlineQuantities, confirmingDeleteKey,
    onQuantityChange, onSaveEdit, onDelete, onConfirmDelete, onClearAll, headerActions,
    overriddenKeys, onResetOverride
}) => {
    const { t } = useTranslation();
    const practiceScope = usePracticeScope();
    if (subrecipes.length === 0) return null;

    return (
        <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-4`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <img src="/assets/sub-recipes.svg" alt="sub-recipes" />
                    <span className={`text-[16px] leading-5 font-semibold ${primaryText}`}>{t('costPrice.subrecipes.sectionHeader')}</span>
                </div>
                <div className="flex items-center gap-1">
                    {headerActions}
                    {onClearAll && (
                        <button type="button" onClick={onClearAll} className="p-1 hover:opacity-70 transition-opacity">
                            <DeleteIcon />
                        </button>
                    )}
                </div>
            </div>

            <div className="space-y-3 pt-4">
                {subrecipes.map((recipe) => {
                    // unit cost per portionUnit (1 g / 1 ml / 1 piece) — recipe.totalCost / portionsNeeded
                    const unitPrice = recipe.portionsNeeded > 0 ? recipe.totalCost / recipe.portionsNeeded : 0;
                    const currentQtyStr = inlineQuantities[recipe.subrecipeId] ?? recipe.portionsNeeded.toString();
                    const currentQty = parseFloat(currentQtyStr) || 0;
                    const estimatedCost = unitPrice * currentQty;

                    return (
                        <div
                            key={recipe.subrecipeId}
                            data-onboarding={practiceScope && isPracticeId(recipe.subrecipeId) ? 'cost-dish-subrecipe' : undefined}
                            className={`rounded-lg border py-[11px] pl-3 pr-4 flex flex-col bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}
                        >
                            <div className="mb-2 flex items-center justify-between gap-2">
                                <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText} flex items-center gap-2 flex-wrap`}>
                                    {recipe.name}
                                    {recipe.isOrphan && (
                                        <span className="ml-2 text-[12px] font-normal text-red-500 dark:text-textRed-dark">
                                            {t('costPrice.subrecipes.orphanInlineLabel')}
                                        </span>
                                    )}
                                    <SourceRestaurantBadge restaurantName={recipe.sourceRestaurantName} />
                                </p>
                                <div className="flex items-center gap-2" data-onboarding={practiceScope && isPracticeId(recipe.subrecipeId) ? 'cost-subrecipe-delete' : undefined}>
                                    {confirmingDeleteKey === recipe.subrecipeId ? (
                                        <button type="button" onClick={() => onConfirmDelete(recipe.subrecipeId)} className="h-8 px-3 text-[14px] font-semibold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white rounded-lg transition-colors">
                                            {t('costPrice.subrecipes.removeAction')}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => onDelete(recipe.subrecipeId)}
                                            className="p-1 hover:opacity-70 transition-opacity"
                                        >
                                            <DeleteIcon />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {!recipe.isOrphan && (
                                <>
                                    <div className="flex items-center justify-between gap-3">
                                        <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>{t('costPrice.subrecipes.unitPriceLabel')}</p>
                                        <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                            {formatSubrecipeUnitPrice(unitPrice, recipe.portionsUnit)}
                                        </p>
                                    </div>

                                    {/* Quantity (tag + reset appear when this dish overrides the shared value) */}
                                    <div className="flex items-center justify-between gap-3 mt-2">
                                        <div className="flex items-center gap-1.5">
                                            <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>{t('costPrice.subrecipes.quantityLabel')}</p>
                                            {overriddenKeys?.has(recipe.subrecipeId) && <OverrideTag />}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {overriddenKeys?.has(recipe.subrecipeId) && onResetOverride && (
                                                <OverrideResetButton onClick={() => onResetOverride(recipe.subrecipeId)} />
                                            )}
                                            <input
                                                type="number" inputMode="decimal" min={0} step="any"
                                                value={currentQtyStr}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) onQuantityChange(recipe.subrecipeId, value);
                                                }}
                                                onBlur={() => onSaveEdit(recipe.subrecipeId, currentQtyStr, recipe.portionsNeeded)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            />
                                            <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                                {getPortionsUnitDisplay(recipe.portionsUnit)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center gap-2 mt-2">
                                        <p className={`text-[14px] leading-[21px] font-normal tracking-[-0.14px] ${greyTextColor}`}>{t('costPrice.subrecipes.costLabel')}</p>
                                        <p className={`text-[14px] leading-5 font-medium ${textBlue}`}>{eur(estimatedCost)}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SubrecipesSection;
